import * as React from 'react';
import { Download, Loader2, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Combobox } from '@/components/ui/combobox';
import { useCreateExport } from '@/hooks/exports/use-create-export';
import { useExports } from '@/hooks/exports/use-exports';
import { useExportDownloadUrl } from '@/hooks/exports/use-export-download-url';
import { triggerDownload } from '@/lib/download';
import { useToast } from '@/hooks/use-toast';
import { EXPORT_TYPE_CODES, type ExportTypeCode, type ExportDto } from '@cutlyft/zod-schemas';

const TYPE_LABELS: Record<ExportTypeCode, string> = {
  CUTTING_LIST_XLSX: 'Cutting List (XLSX)',
  GLASS_LIST_XLSX: 'Glass List (XLSX)',
  CLIENT_QUOTE_PDF: 'Client Quote (PDF)',
  LAYOUT_PDF: 'Layout (PDF)',
};

const ALL_EXPORT_TYPE_OPTIONS = EXPORT_TYPE_CODES.map((code) => ({
  value: code,
  label: TYPE_LABELS[code],
}));

const PROJECT_EXPORT_TYPE_OPTIONS = ALL_EXPORT_TYPE_OPTIONS.filter(
  (o) => o.value !== 'LAYOUT_PDF',
);

interface DownloadButtonProps {
  exportId: number;
}

function DownloadButton({ exportId }: DownloadButtonProps) {
  const { toast } = useToast();
  const { data: url, isLoading, refetch } = useExportDownloadUrl(exportId);

  const handleDownload = async () => {
    const result = await refetch();
    const downloadUrl = result.data;
    if (downloadUrl) {
      triggerDownload(downloadUrl);
    } else {
      toast({ variant: 'destructive', title: 'Could not get download URL' });
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={handleDownload} disabled={isLoading}>
      {isLoading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

interface ExportRowProps {
  export_: ExportDto;
}

function ExportRow({ export_: exp }: ExportRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-3.5 w-3.5 text-fg-muted shrink-0" />
        <span className="text-sm truncate">{TYPE_LABELS[exp.exportType]}</span>
        {exp.fileSizeBytes && (
          <span className="text-xs text-fg-muted shrink-0">
            {(exp.fileSizeBytes / 1024).toFixed(1)} KB
          </span>
        )}
      </div>
      <DownloadButton exportId={exp.id} />
    </div>
  );
}

interface ExportsPanelProps {
  projectId?: number;
  runId?: number;
}

export function ExportsPanel({ projectId, runId }: ExportsPanelProps) {
  const { toast } = useToast();
  const exportTypeOptions = runId ? ALL_EXPORT_TYPE_OPTIONS : PROJECT_EXPORT_TYPE_OPTIONS;
  const [selectedType, setSelectedType] = React.useState<ExportTypeCode>('CUTTING_LIST_XLSX');

  const createExport = useCreateExport();
  const { data, isLoading } = useExports({ projectId, limit: 20 });
  const exports_ = data?.items ?? [];

  const handleCreate = () => {
    createExport.mutate(
      { exportType: selectedType, projectId, runId },
      {
        onSuccess: (result) => {
          toast({ title: 'Export created — downloading…' });
          if (result?.downloadUrl) {
            triggerDownload(result.downloadUrl);
          }
        },
        onError: () => toast({ variant: 'destructive', title: 'Export failed' }),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Exports</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="flex-1">
            <Combobox
              options={exportTypeOptions}
              value={selectedType}
              onValueChange={(v) => v && setSelectedType(v as ExportTypeCode)}
              placeholder="Select export type"
              searchPlaceholder="Search export type..."
              clearable={false}
            />
          </div>
          <Button onClick={handleCreate} disabled={createExport.isPending} size="sm">
            {createExport.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Generate'}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-16">
            <Loader2 className="h-4 w-4 animate-spin text-fg-muted" />
          </div>
        ) : exports_.length === 0 ? (
          <p className="text-sm text-fg-muted">No exports yet.</p>
        ) : (
          <div>
            {exports_.map((exp) => (
              <ExportRow key={exp.id} export_={exp} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
